import { PROJECT_WORKFLOW_LABELS, PROJECT_WORKFLOW_TONES } from './types';
import type { ProjectListItem, ProjectWorkflowStatus } from './types';

/**
 * The Projects board: status columns, category tabs.
 *
 * A deliberate parallel to the Personal Tasks board (features/board), and NOT
 * a reuse of it — that one buckets TASKS by project_statuses rows, this one
 * buckets PROJECTS by the project_status enum. The two look alike on screen
 * and share nothing underneath. Trying to make one function serve both would
 * mean a generic over the row type, the group key and the label lookup, which
 * is more machinery than either side saves.
 *
 * The lesson that did carry over is the shape: columns always mean status, and
 * the other dimension is a filter above the board rather than a rival column
 * layout. Grouping by category INSTEAD of status is what the Personal Tasks
 * board did wrong — it answered one question by throwing away the other.
 *
 * Columns come from the enum in its own order, not from what happens to be in
 * the data. An empty "On Hold" is information — nothing is stuck — and it is
 * also where a card would be dropped if dragging is added later.
 */

/**
 * Lifecycle order, and every status must appear.
 *
 * buildProjectColumns maps over this list, so a status missing here has no
 * column — and a project in that status does not move, it DISAPPEARS from the
 * board with nothing to say it did. That is the worst shape this bug can take,
 * and it is one hand-written array away at all times.
 *
 * Typed as a Record rather than a bare array so the compiler counts for us:
 * adding a value to the project_status enum now fails the build here until
 * somebody decides where in the lifecycle it belongs. There is a test that
 * asserts the same thing at runtime, because a Record key order is a
 * convention and the order is the whole point of this list.
 */
const STATUS_RANK: Record<ProjectWorkflowStatus, number> = {
  not_started: 1,
  in_progress: 2,
  // Review before revision: work is looked at, and only then does it come
  // back needing changes. The compiler forced this decision rather than
  // letting the new status default to the end of the board, which is exactly
  // what this Record exists to do.
  for_review: 3,
  for_revision: 4,
  on_hold: 5,
  completed: 6,
};

export const PROJECT_STATUS_ORDER: ProjectWorkflowStatus[] = (
  Object.keys(STATUS_RANK) as ProjectWorkflowStatus[]
).sort((left, right) => STATUS_RANK[left] - STATUS_RANK[right]);

export interface ProjectBoardColumn {
  key: ProjectWorkflowStatus;
  name: string;
  // Mirrors PROJECT_WORKFLOW_TONES rather than restating a narrower list.
  // Kept in step deliberately: this column type existed with four tones while
  // the map it is built from grew a fifth, and a board column cannot have a
  // tone the map cannot produce.
  tone: (typeof PROJECT_WORKFLOW_TONES)[ProjectWorkflowStatus];
  projects: ProjectListItem[];
}

export const ALL_CATEGORIES_TAB_KEY = 'all';
export const UNCATEGORIZED_TAB_KEY = 'uncategorized';

export interface CategoryTab {
  /** 'all', 'uncategorized', or the lower-cased category name. */
  key: string;
  name: string;
  count: number;
  /**
   * What the filter becomes. undefined clears it; '' is the real choice
   * "projects with no category", which the data stores as an empty string
   * rather than null.
   */
  category: string | undefined;
}

/** Case-insensitive, because "Sales" and "sales" are one category. */
function sameCategory(left: string, right: string): boolean {
  return left.trim().toLocaleLowerCase() === right.trim().toLocaleLowerCase();
}

/**
 * Projects matching the category filter. undefined means everything.
 *
 * Kept separate from the column building so the tab counts can be taken from
 * the UNFILTERED list — a tab that reported how many projects it would show
 * after its own filter had been applied would read zero for every tab except
 * the selected one.
 */
export function filterByCategory(
  projects: ProjectListItem[],
  category: string | undefined,
): ProjectListItem[] {
  if (category === undefined) {
    return projects;
  }
  return projects.filter((project) => sameCategory(project.category, category));
}

/**
 * One column per status, in lifecycle order, newest first inside each.
 *
 * Sorted by updated_at rather than name: a board is for seeing what moved, and
 * the thing that moved most recently is the thing somebody is asking about.
 */
export function buildProjectColumns(projects: ProjectListItem[]): ProjectBoardColumn[] {
  return PROJECT_STATUS_ORDER.map((status) => ({
    key: status,
    name: PROJECT_WORKFLOW_LABELS[status],
    tone: PROJECT_WORKFLOW_TONES[status],
    projects: projects
      .filter((project) => project.status === status)
      .slice()
      .sort((left, right) => right.updated_at.localeCompare(left.updated_at)),
  }));
}

/**
 * The tab strip: All, then one per category in alphabetical order, then
 * Uncategorized last if anything is in it.
 *
 * Uncategorized goes at the END rather than leading, unlike the list view's
 * grouping. On the list it leads because that is where a new project lands and
 * somebody has to find it; as a filter tab it is the least specific thing on
 * the strip and belongs after the named ones.
 */
export function categoryTabs(projects: ProjectListItem[]): CategoryTab[] {
  const named = new Map<string, { label: string; count: number }>();
  let loose = 0;

  for (const project of projects) {
    const category = project.category.trim();
    if (category === '') {
      loose += 1;
      continue;
    }
    const key = category.toLocaleLowerCase();
    const existing = named.get(key);
    if (existing === undefined) {
      // First spelling encountered wins, matching groupProjectsByCategory.
      named.set(key, { label: category, count: 1 });
    } else {
      existing.count += 1;
    }
  }

  const tabs: CategoryTab[] = [
    {
      key: ALL_CATEGORIES_TAB_KEY,
      name: 'All',
      count: projects.length,
      category: undefined,
    },
    ...[...named.entries()]
      .sort((left, right) => left[1].label.localeCompare(right[1].label))
      .map(([key, value]) => ({
        key,
        name: value.label,
        count: value.count,
        category: value.label,
      })),
  ];

  if (loose > 0) {
    tabs.push({
      key: UNCATEGORIZED_TAB_KEY,
      name: 'Uncategorized',
      count: loose,
      category: '',
    });
  }

  return tabs;
}

/** Which tab is lit. Nothing set means All; '' is Uncategorized. */
export function activeCategoryKey(category: string | undefined): string {
  if (category === undefined) {
    return ALL_CATEGORIES_TAB_KEY;
  }
  return category.trim() === '' ? UNCATEGORIZED_TAB_KEY : category.trim().toLocaleLowerCase();
}

/**
 * Free-text search across name, description and category.
 *
 * All three because the question this answers is "where did that old project
 * go" — and a year later the thing somebody remembers is as often a phrase
 * from the description as the name. Category is in the net too, which does
 * overlap the pills; the pills are for browsing a known category, this is for
 * finding one you half-remember.
 *
 * Client-side over the already-loaded list, so it filters as you type. Case
 * and surrounding space are ignored; an empty query is not a filter.
 */
export function searchProjects(projects: ProjectListItem[], query: string): ProjectListItem[] {
  const needle = query.trim().toLocaleLowerCase();
  if (needle === '') {
    return projects;
  }
  return projects.filter((project) =>
    [project.name, project.description ?? '', project.category]
      .join(' ')
      .toLocaleLowerCase()
      .includes(needle),
  );
}
